import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";
import { PageHeader, Panel, Empty } from "@/components/Shell";
import { UsersPanel } from "@/components/UsersPanel";

export default function Settings() {
  const { canWrite, user } = useAuth();
  const [form, setForm] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/company").then(({ data }) => setForm(data));
    api.get("/email-logs").then(({ data }) => setLogs(data));
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(data); toast.success("Logo updated");
    } catch (err) { toast.error(apiError(err)); }
  };
  const removeLogo = async () => {
    try { const { data } = await api.delete("/company/logo"); setForm(data); toast.success("Logo removed"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { id, _id, ...payload } = form;
      await api.put("/company", { ...payload, vat_rate: Number(payload.vat_rate), default_wht_rate: Number(payload.default_wht_rate) });
      toast.success("Company settings saved");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!form) return <p className="text-sm text-slate-500">Loading settings…</p>;

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Company profile, URA identifiers and tax defaults." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Company profile" className="lg:col-span-2">
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
            {[["Company name", "name"], ["TIN", "tin"], ["VAT number", "vat_number"],
              ["Phone", "phone"], ["Email", "email"], ["Currency", "currency"],
              ["Address", "address"]].map(([label, key]) => (
              <div key={key} className={key === "address" ? "sm:col-span-2" : ""}>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
                <input data-testid={`company-${key}-input`} value={form[key] || ""} disabled={!canWrite}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50" />
              </div>
            ))}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">VAT rate (%)</label>
              <input data-testid="company-vat-rate-input" type="number" step="any" value={form.vat_rate} disabled={!canWrite}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Default WHT rate (%)</label>
              <input data-testid="company-wht-rate-input" type="number" step="any" value={form.default_wht_rate} disabled={!canWrite}
                onChange={(e) => setForm({ ...form, default_wht_rate: e.target.value })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50" />
            </div>
            {canWrite && (
              <button data-testid="save-company-button" className="sm:col-span-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                Save settings
              </button>
            )}
          </form>
        </Panel>

        <Panel title="Your access">
          <div className="space-y-3 p-5 text-sm">
            <div>
              <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Organisation logo</p>
              <div className="flex items-center gap-4">
                <div className="grid h-20 w-32 place-items-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50">
                  {form.logo_url
                    ? <img src={form.logo_url} alt="Logo" data-testid="company-logo-preview" className="max-h-20 max-w-full object-contain" />
                    : <span className="text-xs text-slate-400" data-testid="company-logo-empty">No logo</span>}
                </div>
                {user?.role === "admin" && (
                  <div className="flex flex-col gap-2">
                    <label className="cursor-pointer rounded-md bg-slate-900 px-3 py-1.5 text-center text-xs font-semibold text-white hover:bg-slate-800">
                      {form.logo_url ? "Replace logo" : "Upload logo"}
                      <input data-testid="company-logo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
                    </label>
                    {form.logo_url && (
                      <button data-testid="remove-company-logo-button" onClick={removeLogo} className="text-xs font-semibold text-rose-600 hover:underline">Remove</button>
                    )}
                    <p className="text-[11px] text-slate-400">PNG, JPG or WEBP · max 2 MB · shown on invoices, quotations and emails</p>
                  </div>
                )}
              </div>
            </div>
            <div><p className="text-xs uppercase tracking-widest text-slate-500">Signed in as</p><p className="font-semibold text-slate-900">{user?.name}</p></div>
            <div><p className="text-xs uppercase tracking-widest text-slate-500">Email</p><p className="text-slate-700">{user?.email}</p></div>
            <div><p className="text-xs uppercase tracking-widest text-slate-500">Role</p><p className="font-semibold capitalize text-emerald-700">{user?.role}</p></div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-800">Uganda tax defaults</p>
              <p className="mt-1">VAT 18% · WHT 6% goods &amp; services, 15% professional fees</p>
              <p>NSSF: 5% employee, 10% employer · PAYE per URA monthly bands</p>
            </div>
          </div>
        </Panel>

        {user?.role === "admin" && <UsersPanel />}

        <Panel title={`Email activity · ${logs.length}`} className="lg:col-span-3">
          {logs.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-logs-table">
                <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="px-5 py-2.5">Sent</th><th className="px-5 py-2.5">Invoice</th>
                    <th className="px-5 py-2.5">Type</th><th className="px-5 py-2.5">Recipient</th>
                    <th className="px-5 py-2.5">Subject</th><th className="px-5 py-2.5">Sent by</th></tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-5 py-2 text-slate-600">{shortDate(l.created_at)}</td>
                      <td className="px-5 py-2 font-semibold text-slate-900">{l.invoice_number}</td>
                      <td className="px-5 py-2 capitalize text-slate-600">{l.kind}</td>
                      <td className="px-5 py-2 text-slate-700">{l.to}</td>
                      <td className="px-5 py-2 text-slate-500">{l.subject}</td>
                      <td className="px-5 py-2 text-slate-500">{l.sent_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty message="No invoice emails have been sent yet." />}
        </Panel>
      </div>
    </div>
  );
}
